// lib/error.ts
import { ApiError } from '@/api/client'
import { toast } from 'sonner'

/**
 * 从错误对象中提取错误消息
 */
export function getErrorMessage(error: unknown, defaultMessage: string = '操作失败'): string {
  if (error instanceof ApiError) {
    return error.message || defaultMessage
  }
  if (error instanceof Error) {
    return error.message || defaultMessage
  }
  if (typeof error === 'string') {
    return error
  }
  return defaultMessage
}

/**
 * 判断是否为登录过期错误
 */
export function isAuthExpiredError(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 10002 || error.status === 401)
}

/**
 * 显示错误提示
 */
export function showError(error: unknown, defaultMessage: string = '操作失败'): void {
  if (isAuthExpiredError(error)) {
    return
  }
  const message = getErrorMessage(error, defaultMessage)
  toast.error(message)
}

/**
 * 显示成功提示
 */
export function showSuccess(message: string): void {
  toast.success(message)
}

/**
 * 包装异步操作，自动处理错误提示
 */
export async function withErrorHandling<T>(
  fn: () => Promise<T>,
  options: {
    successMessage?: string
    defaultErrorMessage?: string
    onSuccess?: (result: T) => void
    onError?: (error: unknown) => void
  } = {}
): Promise<T | null> {
  try {
    const result = await fn()
    if (options.successMessage) {
      showSuccess(options.successMessage)
    }
    options.onSuccess?.(result)
    return result
  } catch (error) {
    showError(error, options.defaultErrorMessage)
    options.onError?.(error)
    return null
  }
}
